/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Pavel Reznak (reznak@cadwork.cz)
 *
 */

// jscs:disable requireCamelCaseOrUpperCaseIdentifiers

import Pass from "App/render/webgl/passes/pass";
import WebGLRenderGraph from "App/render/webgl/render_graph";
import {vec3} from "gl-matrix";
import WebGLContext from "App/web_gl_context";
import View from "App/view/view";
import {Framebuffer} from "App/utils/frame_buffer";

import AOShader from "App/render/webgl/shaders/ssao/ao"
import {Containers, Factory} from "App/utils/factory";
import IScene from "js_interface/src/scene_interface";

/**
 * @param {SceneAPI.Scene} aSceneAPI
 * @param {WebGLRenderGraph} aRenderGraph
 * @class
 * @extends Pass
 */
export default
class AO extends Pass {

    public static TypeName: string = "AO";
    private _quadVBO: WebGLBuffer;
    private _noiseTexture: WebGLTexture;
    private _kernelSize: number;
    private _kernelSamples: vec3[] = null;

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph) {
        super(aSceneAPI, aRenderGraph);

        // VBOs
        this._quadVBO = null;

        // Textures
        this._noiseTexture = null;

        // SSAO kernel samples
        this._kernelSize = 16;
        this._kernelSamples = null;
    }

    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph): AO {
        return new AO(aSceneAPI, aRenderGraph);
    }

    /**
     * Initialize values
     */
    public initialize() {
        if (!this._sceneAPI.lightingModule.getSSAO().isEnabled()) {
            return;
        }

        this.createQuadVBO();
        this.createSampleKernel();
        this.createNoiseTexture();
    }

    public release() {
        super.release();

        let gl = this._context.getWebGLRenderingContext();
        gl.deleteBuffer(this._quadVBO);
        this._quadVBO = null;

        gl.deleteTexture(this._noiseTexture);
        this._noiseTexture = null;
    }

    /**
     * Support function for creating kernel samples
     *
     * @param {number} a
     * @param {number} b
     * @param {number} f
     * @returns {number}
     */
    public lerp(a: number, b: number, f: number): number {
        return a + f * (b - a);
    }

    /**
     * Create kernel samples for SSAO
     */
    public createSampleKernel() {
        this._kernelSamples = [];
        for (let i = 0; i < this._kernelSize; ++i) {
            let sample = vec3.create();
            sample[0] = Math.random() * 2.0 - 1.0;
            sample[1] = Math.random() * 2.0 - 1.0;
            sample[2] = Math.random();

            vec3.normalize(sample, sample);

            // Use random scale OR
            /*let scale = Math.random();
            sample[0] *= scale;
            sample[1] *= scale;
            sample[2] *= scale;*/

            // Do it better -> Align more kernels to the center
            let scale = i / this._kernelSize;
            scale = this.lerp(0.1, 1.0, scale * scale);
            sample[0] *= scale;
            sample[1] *= scale;
            sample[2] *= scale;

            this._kernelSamples[i] = sample;
        }
    }

    /**
     * Create small random noise texture
     */
    public createNoiseTexture() {
        let gl = this._context.getWebGLRenderingContext();
        let data = [];
        for (let i = 0; i < 16 * 3; i += 3) {
            data[i] = Math.random() * 2.0 - 1.0;
            data[i + 1] = Math.random() * 2.0 - 1.0;
            data[i + 2] = 0.0;
        }

        this._noiseTexture = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, this._noiseTexture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
        // @ts-ignore
        gl.texImage2D(gl.TEXTURE_2D, 0, WebGLContext.isWebGL2 ? gl.RGB32F : gl.RGB, 4, 4, 0, gl.RGB, gl.FLOAT, new Float32Array(data));
        gl.bindTexture(gl.TEXTURE_2D, null);
    }

    /**
     * Create quad VBO
     */
    public createQuadVBO() {
        // Full Screen Quad
        let vertices = [
            // Positions     // Texture Coords
            -1.0, 1.0, 0.0, 0.0, 1.0,
            -1.0, -1.0, 0.0, 0.0, 0.0,
            1.0, 1.0, 0.0, 1.0, 1.0,
            1.0, -1.0, 0.0, 1.0, 0.0
        ];

        let gl = this._context.getWebGLRenderingContext();
        this._quadVBO = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this._quadVBO);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(vertices), gl.STATIC_DRAW);
    }

    /**
     * Calculate AO from positions and normals.
     * This uses vec4ToFloat and floatToVec4 to reduce memory consumption.
     *
     * @param {View}              aView
     * @param {Framebuffer} aOutput           Unsigned byte, RGBA
     * @param {WebGLTexture}                    aGeometryTexture  Float, RGB(A)
     * @param {WebGLTexture}                    aNormalsTexture   Float, RGB(A)
     */
    public render(aView: View, aOutput: Framebuffer, aGeometryTexture: WebGLTexture, aNormalsTexture: WebGLTexture) {
        /* jshint camelcase:false */
        this.view = aView;
        this.state.resetRenderNodesFilters();

        let gl = this._context.getWebGLRenderingContext();
        let camera = aView.getCamera();
        let scale = aOutput.getParameters().scale;
        let position = aView.getPosition();
        let size = aView.getSize();

        gl.viewport(position[0] * scale, position[1] * scale, size[0] * scale, size[1] * scale);
        gl.scissor(position[0] * scale, position[1] * scale, size[0] * scale, size[1] * scale);

        aOutput.bind();
        gl.clear(gl.COLOR_BUFFER_BIT);

        // bind shader
        let shaders = this._renderGraph.getLayer('ssao').getShaders();
        this._activeShader = shaders.get(AOShader.TypeName);
        this._activeShaderRenderable = this._activeShader.get(0);
        this._activeShaderRenderable.render();

        this.state.setRenderablesFilters(this._activeShaderRenderable.getRenderablesFilters());

        // Render quad
        gl.bindBuffer(gl.ARRAY_BUFFER, this._quadVBO);

        // Textures
        let uniformLocations = this._activeShaderRenderable.getUniformLocations();

        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, aGeometryTexture);
        gl.uniform1i(uniformLocations.u_texture, 0);
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, aNormalsTexture);
        gl.uniform1i(uniformLocations.u_normals, 1);
        gl.activeTexture(gl.TEXTURE2);
        gl.bindTexture(gl.TEXTURE_2D, this._noiseTexture);
        gl.uniform1i(uniformLocations.u_noise, 2);

        // Uniforms
        gl.uniform1f(uniformLocations.u_radius, this._sceneAPI.lightingModule.getSSAO().getRadius());
        gl.uniform1f(uniformLocations.u_bias, this._sceneAPI.lightingModule.getSSAO().getBias());
        gl.uniformMatrix4fv(uniformLocations.u_view_matrix, false, aView.getCamera().getViewMatrix());

        let canvas = this._context.getCanvas();
        let contentSize = canvas.getContentSize();
        gl.uniform2f(uniformLocations.u_buffer_size, contentSize[0], contentSize[1]);
        gl.uniform4f(this._activeShaderRenderable.getUniformLocations().u_view,
            position[0] * scale / contentSize[0],
            position[1] * scale / contentSize[1],
            size[0] * scale / contentSize[0],
            size[1] * scale / contentSize[1]
        );

        gl.uniform2f(uniformLocations.u_noise_scale, contentSize[0] / 4.0, contentSize[1] / 4.0);
        gl.uniformMatrix4fv(uniformLocations.u_projection, false, camera.getProjectionMatrixRef());
        for (let i = 0; i < this._kernelSize; ++i) {
            let location = gl.getUniformLocation(this._activeShaderRenderable.getProgram(), "u_samples[" + i + "]");
            gl.uniform3fv(location, this._kernelSamples[i]);
        }

        // Attributes
        let attributeLocations = this._activeShaderRenderable.getAtttributesLocations();
        this._activeShaderRenderable.enableAttributes();
        gl.vertexAttribPointer(attributeLocations.a_position, 3, gl.FLOAT, false, 5 * 4, 0);
        gl.vertexAttribPointer(attributeLocations.a_texcoord, 2, gl.FLOAT, false, 5 * 4, 3 * 4);

        // Draw and release
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        gl.bindBuffer(gl.ARRAY_BUFFER, null);
        this._activeShaderRenderable.finishRender();

        // Reset
        this.view = null;
    }
}

Factory.register(Containers.PASSES_SSAO, AO);
